/**
 * By Lucia PDP — interactions
 *  - Gallery thumbnail swap (image / video)
 *  - Quantity controls
 *  - Variant swatch switching (resolves variant via /products/{handle}.js)
 *  - FBT bundle add-to-cart (single /cart/add.js POST)
 */
(function () {
  'use strict';

  var moneyFormat = (window.Shopify && window.Shopify.money_format) || '${{amount}}';

  function formatMoney(cents) {
    if (typeof cents !== 'number') cents = parseInt(cents, 10);
    if (isNaN(cents)) return '';
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyFormat
      .replace('{{amount}}', whole + '.' + parts[1])
      .replace('{{amount_no_decimals}}', whole)
      .replace('{{amount_with_comma_separator}}', whole + ',' + parts[1])
      .replace('{{amount_no_decimals_with_comma_separator}}', whole);
  }

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ─── Cart: AJAX add + open the native drawer ──────────────────
   * Replaces the form's native POST-to-/cart redirect. We add via
   * /cart/add.js (which Klaviyo's onsite tracking hooks for the
   * "Added to Cart" / active-on-site event), fire Horizon's own
   * `cart:update` event so the cart items + icon re-render, then call
   * the cart drawer's public open() method directly. The drawer also
   * carries the `auto-open` attribute now, but we call open() too so
   * the behaviour never depends on that attribute alone.
   */
  function cartSectionIds() {
    return $$('cart-items-component')
      .map(function (el) { return el.getAttribute('data-section-id'); })
      .filter(Boolean);
  }

  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return true;
    }
    return false;
  }

  function dispatchCartUpdate(cart, sections, sourceId) {
    // Mirrors Horizon's CartAddEvent: type 'cart:update', detail shape
    // { resource, sourceId, data: { source, sections } }.
    var detail = {
      resource: cart,
      sourceId: sourceId,
      data: { source: 'product-form-component', sections: sections },
    };
    var evt;
    try {
      evt = new CustomEvent('cart:update', { bubbles: true, detail: detail });
    } catch (e) {
      evt = document.createEvent('CustomEvent');
      evt.initCustomEvent('cart:update', true, false, detail);
    }
    document.dispatchEvent(evt);
  }

  // body: { id, quantity } or { items: [...] }. onError(payload) optional.
  function addToCart(body, sourceId, onError) {
    var ids = cartSectionIds();
    if (ids.length) {
      body.sections = ids.join(',');
    }
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(function (r) {
        return r.json().then(function (data) {
          return { ok: r.ok, data: data };
        });
      })
      .then(function (res) {
        if (!res.ok || res.data.status) {
          if (onError) onError(res.data);
          return null;
        }
        // Refresh drawer line items + cart icon from the add response's
        // rendered sections, then open the drawer. No second /cart.js
        // round-trip — cart-items re-renders itself if sections is absent.
        dispatchCartUpdate(res.data, res.data.sections, sourceId);
        if (!openCartDrawer()) {
          window.location.href = '/cart';
        }
        return res.data;
      });
  }

  /* ─── Gallery: scroll-snap swipe + thumb sync + tap-to-zoom ──── */
  var zoomDialog = null;

  function openZoom(src, alt) {
    if (!zoomDialog) {
      zoomDialog = document.createElement('dialog');
      zoomDialog.className = 'gallery-zoom';
      zoomDialog.innerHTML =
        '<button type="button" class="gallery-zoom__close" aria-label="Close">×</button>' +
        '<div class="gallery-zoom__scroller"><img class="gallery-zoom__img" alt=""></div>';
      document.body.appendChild(zoomDialog);
      zoomDialog.querySelector('.gallery-zoom__close')
        .addEventListener('click', function () { closeZoom(); });
      // Tap the backdrop / margins (not the image) to dismiss.
      zoomDialog.addEventListener('click', function (e) {
        if (e.target === zoomDialog || e.target.classList.contains('gallery-zoom__scroller')) closeZoom();
      });
      zoomDialog.addEventListener('close', function () {
        document.documentElement.classList.remove('pdp-zoom-open');
      });
    }
    var zimg = zoomDialog.querySelector('.gallery-zoom__img');
    zimg.src = src;
    zimg.alt = alt || '';
    if (typeof zoomDialog.showModal === 'function' && !zoomDialog.open) {
      zoomDialog.showModal();
    } else {
      zoomDialog.setAttribute('open', '');
    }
    document.documentElement.classList.add('pdp-zoom-open');
  }

  function closeZoom() {
    if (!zoomDialog) return;
    if (typeof zoomDialog.close === 'function' && zoomDialog.open) zoomDialog.close();
    else zoomDialog.removeAttribute('open');
    document.documentElement.classList.remove('pdp-zoom-open');
  }

  function initGallery(root) {
    var track = $('[data-gallery-main]', root);
    if (!track) return;
    var slides = $$('[data-gallery-slide]', track);
    var thumbs = $$('[data-gallery-thumb]', root);
    if (!slides.length) return;

    function slideByIndex(index) {
      for (var i = 0; i < slides.length; i++) {
        if (parseInt(slides[i].getAttribute('data-index'), 10) === index) return slides[i];
      }
      return null;
    }

    function setActiveThumb(index) {
      thumbs.forEach(function (t) {
        t.classList.toggle('active', parseInt(t.getAttribute('data-index'), 10) === index);
      });
    }

    // Thumbnail click → scroll the track to that slide (desktop behaviour).
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var index = parseInt(thumb.getAttribute('data-index'), 10);
        var slide = slideByIndex(index);
        if (!slide) return;
        track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
        setActiveThumb(index);
      });
    });

    // Swiping/scrolling the track → keep the active thumbnail in sync.
    var raf = null;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = window.requestAnimationFrame(function () {
        raf = null;
        var center = track.scrollLeft + track.clientWidth / 2;
        var best = null, bestDist = Infinity;
        slides.forEach(function (s) {
          var d = Math.abs((s.offsetLeft + s.offsetWidth / 2) - center);
          if (d < bestDist) { bestDist = d; best = s; }
        });
        if (best) setActiveThumb(parseInt(best.getAttribute('data-index'), 10));
      });
    }, { passive: true });

    // Tap an image slide → open the zoom lightbox (video slides excluded).
    slides.forEach(function (slide) {
      if (slide.classList.contains('gallery__slide--video')) return;
      slide.addEventListener('click', function () {
        var src = slide.getAttribute('data-zoom-src');
        if (!src) return;
        var img = slide.querySelector('img');
        openZoom(src, img ? img.alt : '');
      });
    });
  }

  /* ─── Quantity ─────────────────────────────────────────────── */
  function initQty(root) {
    var input = $('[data-qty-input]', root);
    if (!input) return;
    var dec = $('[data-qty-decrement]', root);
    var inc = $('[data-qty-increment]', root);
    if (dec) dec.addEventListener('click', function () {
      var v = parseInt(input.value, 10) || 1;
      if (v > 1) input.value = v - 1;
    });
    if (inc) inc.addEventListener('click', function () {
      var v = parseInt(input.value, 10) || 1;
      input.value = v + 1;
    });
  }

  /* ─── Variant swatches ────────────────────────────────────── */
  function initVariants(root) {
    var form = $('[data-product-form]', root);
    if (!form) return;
    var handle = form.getAttribute('data-product-handle');
    if (!handle) return;

    var productData = null;
    function loadProduct() {
      if (productData) return Promise.resolve(productData);
      return fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (data) { productData = data; return data; });
    }

    function selectedOptions() {
      return $$('[data-option-group]', root).map(function (group) {
        // Size options render as a <select>; others as .swatch pills.
        var select = group.querySelector('[data-variant-select]');
        if (select) return select.value;
        var active = $('[data-swatch].active', group);
        return active ? active.getAttribute('data-value') : null;
      });
    }

    function findVariant(product) {
      var selected = selectedOptions();
      return product.variants.find(function (v) {
        return v.options.every(function (opt, i) {
          return !selected[i] || opt === selected[i];
        });
      });
    }

    function updateForVariant(variant) {
      var idInput = $('[data-variant-id]', root);
      var priceEl = $('[data-variant-price]', root);
      var afterpayEl = $('[data-variant-afterpay]', root);
      var compareEl = $('[data-variant-compare]', root);
      var atcBtn = $('[data-atc-button]', root);

      if (!variant) {
        if (atcBtn) {
          atcBtn.disabled = true;
          atcBtn.textContent = 'Unavailable';
        }
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }
      if (afterpayEl) {
        var quarter = Math.round(variant.price / 4);
        afterpayEl.innerHTML = 'or 4 interest-free payments of <strong>' + formatMoney(quarter) + '</strong> with Afterpay';
      }
      // Pre-order: prefer the server-resolved per-variant state map (the only
      // reliable source — /products.js omits inventory_quantity); fall back to a
      // client resolve. Updates eyebrow / ship / button / line-item props.
      var poForm = $('[data-product-form]', root);
      var poMode = (poForm && poForm.getAttribute('data-po-mode')) || 'auto';
      var poState = readPoStates(root)[variant.id] || resolvePreorderState(variant, poMode);
      applyPreorderState(root, poState);

      // Pre-release discount caption + ⓘ discount line: pre-order state AND a
      // compare-at RRP above the price (shows the instant a discount is set).
      var poDiscount = poState === 'preorder' && variant.compare_at_price && variant.compare_at_price > variant.price;
      $$('[data-po-discount]', root).forEach(function (el) { el.hidden = !poDiscount; });

      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {}
    }

    $$('[data-option-group]', root).forEach(function (group) {
      var optionIndex = parseInt(group.getAttribute('data-option-index'), 10);
      var labelValue = $('[data-variant-label-value][data-option-index="' + optionIndex + '"]', root);
      $$('[data-swatch]', group).forEach(function (sw) {
        sw.addEventListener('click', function () {
          $$('[data-swatch]', group).forEach(function (s) { s.classList.remove('active'); });
          sw.classList.add('active');
          if (labelValue) labelValue.textContent = '— ' + sw.getAttribute('data-value');
          loadProduct().then(function (p) { updateForVariant(findVariant(p)); });
        });
      });

      // Size options use a <select> instead of pills.
      var select = group.querySelector('[data-variant-select]');
      if (select) {
        select.addEventListener('change', function () {
          if (labelValue) labelValue.textContent = '— ' + select.value;
          loadProduct().then(function (p) { updateForVariant(findVariant(p)); });
        });
      }
    });
  }

  /* ─── Main add-to-cart ─────────────────────────────────────── */
  function initMainAtc(root) {
    var form = $('[data-product-form]', root);
    if (!form) return;
    var btn = $('[data-atc-button]', root);

    form.addEventListener('submit', function (e) {
      // Let the dynamic checkout / Shop Pay button (rendered by
      // {{ form | payment_button }}) submit normally to checkout.
      var submitter = e.submitter;
      if (submitter && submitter !== btn && !submitter.hasAttribute('data-atc-button')) {
        return;
      }
      e.preventDefault();
      if (btn && btn.disabled) return;

      var idInput = $('[data-variant-id]', root);
      var qtyInput = $('[data-qty-input]', root);
      var id = idInput ? idInput.value : new FormData(form).get('id');
      var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
      if (!id) return;

      // Required initial: if the selector is present, a letter MUST be chosen.
      // A plain `required` attribute can't stop an AJAX add, so block here.
      var initialWrap = $('[data-initial-select]', root);
      var initialValue = '';
      if (initialWrap) {
        var initInput = $('[data-initial-input]', initialWrap);
        initialValue = initInput && initInput.value ? initInput.value : '';
        if (!initialValue) {
          initialWrap.classList.add('error');
          var help = $('[data-initial-help]', initialWrap);
          if (help) help.textContent = 'Please choose your initial before adding to cart.';
          (initialWrap.querySelector('[data-initial-grid]') || initialWrap)
            .scrollIntoView({ block: 'center', behavior: 'smooth' });
          return;
        }
      }

      var body = { id: parseInt(id, 10), quantity: qty };
      var props = {};
      if (initialValue) props.Initial = initialValue;
      // Customer-visible pre-order / made-to-order cart line: include the enabled
      // hidden property input (the AJAX add is built manually, so form
      // properties[...] inputs aren't submitted automatically).
      [$('[data-po-prop-preorder]', root), $('[data-po-prop-mto]', root)].forEach(function (el) {
        if (!el || el.disabled) return;
        var m = (el.name || '').match(/^properties\[(.+)\]$/);
        if (m) props[m[1]] = el.value;
      });
      if (Object.keys(props).length) body.properties = props;

      var restoreText = btn ? btn.textContent : '';
      function restore() {
        if (btn) { btn.disabled = false; btn.textContent = restoreText || 'Add to cart'; }
      }
      if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

      addToCart(body, 'bylucia-pdp-form', function (err) {
        restore();
        if (err && (err.description || err.message)) {
          console.warn('[pdp] add to cart failed:', err.description || err.message);
        }
      })
        .then(function (cart) { if (cart) restore(); })
        .catch(restore);
    });
  }

  /* ─── Initial selector (A–Z) ───────────────────────────────── */
  function initInitial(root) {
    var wrap = $('[data-initial-select]', root);
    if (!wrap) return;
    var input = $('[data-initial-input]', wrap);
    var label = $('[data-initial-label]', wrap);
    var help = $('[data-initial-help]', wrap);
    var helpDefault = help ? help.textContent : '';
    var pills = $$('[data-initial-pill]', wrap);

    pills.forEach(function (pill) {
      pill.addEventListener('click', function () {
        pills.forEach(function (p) {
          p.classList.remove('active');
          p.setAttribute('aria-checked', 'false');
        });
        pill.classList.add('active');
        pill.setAttribute('aria-checked', 'true');

        var letter = pill.getAttribute('data-letter');
        if (input) input.value = letter;
        if (label) label.textContent = '— ' + letter;
        wrap.classList.remove('error');
        if (help) help.textContent = helpDefault;
      });
    });
  }

  /* ─── FBT bundle ATC ───────────────────────────────────────── */
  function initFbtBundle(root) {
    var btn = $('[data-fbt-add-bundle]', root);
    if (!btn) return;
    btn.addEventListener('click', function () {
      var ids = (btn.getAttribute('data-variant-ids') || '').split(',').filter(Boolean);
      if (!ids.length) return;
      var restoreText = btn.textContent;
      function restore() { btn.disabled = false; btn.textContent = restoreText || 'Add all three to cart'; }
      btn.disabled = true;
      btn.textContent = 'Adding…';

      addToCart(
        { items: ids.map(function (id) { return { id: parseInt(id, 10), quantity: 1 }; }) },
        'bylucia-pdp-fbt',
        function () { restore(); }
      )
        .then(function (cart) { if (cart) restore(); })
        .catch(restore);
    });
  }

  /* ─── Pre-order ────────────────────────────────────────────── */
  // Server-resolved per-variant state map (emitted in main-product.liquid).
  var _poStates = null;
  function readPoStates(root) {
    if (_poStates) return _poStates;
    _poStates = {};
    try {
      var el = $('[data-po-states]', root) || document.querySelector('[data-po-states]');
      if (el) _poStates = JSON.parse(el.textContent);
    } catch (e) {}
    return _poStates;
  }

  // Client-side fallback resolve (used only if the SSR map is missing the variant).
  function resolvePreorderState(variant, mode) {
    if (mode === 'notify') return 'notify';
    if (mode === 'made_to_order') return 'mto';
    if (mode === 'on') return 'preorder';
    if (!variant) return 'soldout';
    if (mode === 'off') return variant.available ? 'normal' : 'soldout';
    // auto: in stock → normal; out of stock + continue-selling (still available) →
    // pre-order; out of stock + deny (not available) → sold out.
    if (!variant.available) return 'soldout';
    var tracked = variant.inventory_management != null && variant.inventory_management !== '';
    if (tracked && variant.inventory_quantity <= 0) return 'preorder';
    return 'normal';
  }

  function applyPreorderState(root, state) {
    var isPre = state === 'preorder' || state === 'mto';
    var eyebrow = $('[data-po-eyebrow]', root);
    var ship = $('[data-po-ship]', root);
    var prePropEl = $('[data-po-prop-preorder]', root);
    var mtoPropEl = $('[data-po-prop-mto]', root);
    var atcBtn = $('[data-atc-button]', root);
    var inline = $('[data-po-inline]', root);
    if (eyebrow) eyebrow.hidden = !isPre;
    if (ship) ship.hidden = !isPre;
    if (!isPre && inline) { inline.hidden = true; var ib = $('[data-po-info]', root); if (ib) ib.setAttribute('aria-expanded', 'false'); }
    // Customer-visible cart-line property rides along per state (auto is
    // variant-level, so this keeps the label accurate on variant change).
    if (prePropEl) prePropEl.disabled = state !== 'preorder';
    if (mtoPropEl) mtoPropEl.disabled = state !== 'mto';
    if (atcBtn) {
      if (state === 'soldout') { atcBtn.disabled = true; atcBtn.textContent = 'Sold out'; }
      else if (state === 'preorder') { atcBtn.disabled = false; atcBtn.textContent = 'Pre-order'; }
      else { atcBtn.disabled = false; atcBtn.textContent = 'Add to cart'; }
    }
  }

  function poCompanyId() {
    var scripts = document.querySelectorAll('script[src*="klaviyo.com/onsite/js/"]');
    for (var i = 0; i < scripts.length; i++) {
      var src = scripts[i].getAttribute('src') || '';
      var q = src.match(/[?&]company_id=([A-Za-z0-9]+)/); if (q) return q[1];
      var p = src.match(/\/onsite\/js\/([A-Za-z0-9]+)\//); if (p) return p[1];
    }
    return null;
  }

  function initPreorder(root) {
    // ⓘ details: desktop (>600px) opens the centred <dialog>; ≤600px expands an
    // inline panel under the ship line (no dialog, no backdrop).
    var modal = $('[data-po-modal]', root);
    var infoBtn = $('[data-po-info]', root);
    var inline = $('[data-po-inline]', root);
    var isMobile = function () { return window.matchMedia('(max-width: 600px)').matches; };
    if (infoBtn) {
      infoBtn.addEventListener('click', function () {
        if (isMobile() && inline) {
          var willOpen = inline.hidden;
          inline.hidden = !willOpen;
          infoBtn.setAttribute('aria-expanded', String(willOpen));
        } else if (modal && typeof modal.showModal === 'function') {
          modal.showModal();
        }
      });
    }
    if (modal) {
      var closeBtn = $('[data-po-modal-close]', modal);
      if (closeBtn) closeBtn.addEventListener('click', function () { modal.close(); });
      modal.addEventListener('click', function (e) { if (e.target === modal) modal.close(); });
    }
    if (inline) {
      var inlineClose = $('[data-po-inline-close]', inline);
      if (inlineClose) inlineClose.addEventListener('click', function () {
        inline.hidden = true;
        if (infoBtn) infoBtn.setAttribute('aria-expanded', 'false');
      });
    }

    // Notify Me → inline waitlist form.
    var notify = $('[data-po-notify]', root);
    if (!notify) return;
    var submit = $('[data-po-notify-submit]', notify);
    var emailEl = $('[data-po-notify-email]', notify);
    var rowEl = $('[data-po-notify-row]', notify);
    var okEl = $('[data-po-notify-ok]', notify);
    var errEl = $('[data-po-notify-err]', notify);
    var optinEl = $('[data-po-notify-optin]', notify);
    if (!submit || !emailEl) return;
    function showErr() { if (errEl) errEl.hidden = false; if (okEl) okEl.hidden = true; }
    function showOk() { if (okEl) okEl.hidden = false; if (errEl) errEl.hidden = true; if (rowEl) rowEl.hidden = true; }

    submit.addEventListener('click', function () {
      var email = (emailEl.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { showErr(); return; }
      var variantInput = $('[data-variant-id]', root);
      var variantId = variantInput ? variantInput.value : '';

      // ALWAYS — Klaviyo onsite (same klaviyo.push / public key the subscribe
      // calls use): identify the email + fire the back-in-stock event.
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        try {
          window.klaviyo.push(['identify', { '$email': email }]);
          window.klaviyo.push(['track', 'Requested Back in Stock', {
            ProductTitle: notify.getAttribute('data-po-product') || '',
            ProductID: notify.getAttribute('data-po-product-id') || '',
            VariantID: variantId,
            VariantTitle: notify.getAttribute('data-po-variant-title') || '',
            ProductURL: notify.getAttribute('data-po-product-url') || '',
            Price: parseFloat(notify.getAttribute('data-po-price') || '0')
          }]);
        } catch (e) {}
      }

      var listId = notify.getAttribute('data-po-list-id') || '';
      var newsListId = notify.getAttribute('data-po-newsletter-list-id') || '';
      var companyId = poCompanyId();
      if (!listId || !companyId) { showErr(); return; } // inert until the Waitlist list ID is set
      var wantNews = !!(optinEl && optinEl.checked);

      // One client/subscriptions call per list; props go only on the waitlist call.
      function subscribe(lid, props) {
        var attrs = { email: email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } };
        if (props) attrs.properties = props;
        var payload = { data: { type: 'subscription', attributes: {
          custom_source: 'Pre-order waitlist',
          profile: { data: { type: 'profile', attributes: attrs } }
        }, relationships: { list: { data: { type: 'list', id: lid } } } } };
        return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2025-07-15' },
          body: JSON.stringify(payload)
        });
      }

      // Klaviyo NATIVE back-in-stock subscription for the selected variant — this
      // is what arms the built-in Back in Stock flow to auto-send the restock
      // email. Catalog id = standard Shopify form $shopify:::$default:::<id>
      // (confirmed 202 against the Catalog).
      function bisSubscribe() {
        var payload = { data: {
          type: 'back-in-stock-subscription',
          attributes: { channels: ['EMAIL'], profile: { data: { type: 'profile', attributes: { email: email } } } },
          relationships: { variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } } }
        } };
        return fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + encodeURIComponent(companyId), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2025-07-15' },
          body: JSON.stringify(payload)
        });
      }

      submit.disabled = true;
      var calls = [
        subscribe(listId, {
          waitlist_product: notify.getAttribute('data-po-product') || '',
          waitlist_product_id: notify.getAttribute('data-po-product-id') || '',
          waitlist_variant: variantId,
          notify_signup: true
        }),
        bisSubscribe()
      ];
      if (wantNews && newsListId) calls.push(subscribe(newsListId, null));

      Promise.all(calls).then(function (rs) {
        if (rs[0] && rs[0].ok) { showOk(); } else { submit.disabled = false; showErr(); }
      }).catch(function () { submit.disabled = false; showErr(); });
    });
  }

  /* ─── Gallery video: muted autoplay when scrolled into view ── */
  function initGalleryVideo(root) {
    var track = $('[data-gallery-main]', root);
    var vids = track ? $$('video[data-gallery-video]', track) : [];
    if (!vids.length || !('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting && e.intersectionRatio >= 0.5) {
          var p = v.play(); if (p && p.catch) p.catch(function () {});
        } else { v.pause(); }
      });
    }, { root: track, threshold: [0, 0.5, 1] });
    vids.forEach(function (v) { io.observe(v); });
  }

  function initAll() {
    var page = $('.pdp-page');
    if (!page) return;
    initGallery(page);
    initGalleryVideo(page);
    initQty(page);
    initVariants(page);
    initInitial(page);
    initMainAtc(page);
    initFbtBundle(page);
    initPreorder(page);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
